import "oc-interaction/ios10UserScalablePolyfill";

/**
 * globaler reservierter Drag
 */
let activeDragContext: DragContext | null = null;

export interface DragContext {
    type: string;
    x: number;
    y: number;
    startPosition: { x: number; y: number };
    draggedElement: HTMLElement;
    name: string;
    [key: string]: any; // Allow dynamic properties
}

export type DraggelsInstance = {
    addDragStartListener: (
        element: HTMLElement,
        callback: (dragContext: DragContext, event: PointerEvent) => void,
        options?: any,
    ) => () => void;
    addDragEndListener: (
        callback: (dragContext: DragContext, event: PointerEvent) => void,
        options?: any,
    ) => () => void;
    addDragMoveListener: (
        callback: (dragContext: DragContext, event: PointerEvent) => void,
        options?: any,
    ) => () => void;
    addDragEnterListener: (
        element: HTMLElement,
        callback: (dragContext: DragContext, event: PointerEvent) => void,
        options?: any,
    ) => () => void;
    addDragLeaveListener: (
        element: HTMLElement,
        callback: (dragContext: DragContext, event: PointerEvent) => void,
        options?: any,
    ) => () => void;
    ifActive: (callback: Function) => (...args: any[]) => void;
    claimDragStream: () => void;
    freeDragStream: () => void;
    unclaimDragStream: () => void;
    getGlobalDragContext: () => DragContext | null;
    claimed: () => boolean;
    active: () => boolean;
};

/**
 * Draggels stellt auf Pointer-Events basierende Drag-Events bereit.
 */
export function Draggels(name: string): DraggelsInstance {
    let dragContext: DragContext | null = null;
    let unclaimedDragContext: DragContext | null = null;

    /**
     * registriert einen DragStart-Listener auf einem draggbaren Element
     */
    function addDragStartListener(
        element: HTMLElement,
        callback: (dragContext: DragContext, event: PointerEvent) => void,
        options: any = undefined,
    ) {
        element.style.touchAction = "none";
        element.ondragstart = () => false;

        const listener = (event: PointerEvent) => {
            if (dragContext) {
                return;
            }
            if (event.buttons !== 1) {
                return;
            }
            if (/^(a|input|textarea|select|option|button)$/i.test((event.target as HTMLElement).tagName)) {
                return;
            }

            (event.target as HTMLElement).releasePointerCapture(event.pointerId);

            dragContext = {
                type: "dragStart",
                x: event.pageX,
                y: event.pageY,
                startPosition: { x: event.pageX, y: event.pageY },
                draggedElement: element,
                name,
            };
            callback(dragContext, event);
        };
        element.addEventListener("pointerdown", listener as EventListener, { ...options, passive: false });

        return () => {
            element.removeEventListener("pointerdown", listener as EventListener);
            delete element.ondragstart;
        };
    }

    /**
     * registriert einen DragEnd-Listener auf dem document
     */
    const addDragEndListener = onDocument(createDragListener("pointerup", "dragEnd"));

    /**
     * registriert einen DragMove-Listener auf document
     */
    const addDragMoveListener = onDocument(
        fireOnlyWhenCoordinatesChange(createDragListener("pointermove", "dragMove")),
    );

    /**
     * registriert einen DragEnter-Listener auf einem Drop-Container
     */
    const addDragEnterListener = createDragListener("pointerover", "dragEnter");

    /**
     * registriert einen DragLeave-Listener auf einem Drop-Container
     */
    const addDragLeaveListener = createDragListener("pointerout", "dragLeave");

    function ifActive(callback: Function) {
        return (...args: any[]) => {
            if (active()) {
                callback(...args);
            }
        };
    }

    function claimDragStream() {
        activeDragContext = dragContext;
        unclaimedDragContext = null;
    }

    function freeDragStream() {
        if (claimed()) {
            activeDragContext = null;
        }
    }

    function unclaimDragStream() {
        freeDragStream();
        unclaimedDragContext = activeDragContext;
    }

    function getGlobalDragContext() {
        return activeDragContext;
    }

    function claimed() {
        return (
            activeDragContext != null &&
            dragContext === activeDragContext &&
            unclaimedDragContext !== activeDragContext
        );
    }

    function active() {
        return activeDragContext == null || claimed();
    }

    function createDragListener(pointerEventType: string, dragEventType: string) {
        return (
            element: HTMLElement | Document,
            callback: (dragContext: DragContext, event: PointerEvent) => void,
            options: any = undefined,
        ) => {
            const listener = (event: PointerEvent) => {
                if (dragContext) {
                    dragContext.type = dragEventType;
                    dragContext.x = event.pageX;
                    dragContext.y = event.pageY;
                    callback(dragContext, event);
                }
            };
            element.addEventListener(pointerEventType, listener as EventListener, {
                passive: true,
                capture: true,
                ...options,
            });
            return () => {
                element.removeEventListener(pointerEventType, listener as EventListener);
            };
        };
    }

    window.addEventListener(
        "pointerup",
        (event) => {
            dragContext = null;
            unclaimedDragContext = null;
            activeDragContext = null;
            // @ts-ignore
            event.target?.releasePointerCapture && event.target.releasePointerCapture(event.pointerId);
        },
        { passive: true },
    );

    return {
        addDragStartListener,
        addDragEndListener,
        addDragMoveListener,
        addDragEnterListener,
        addDragLeaveListener,
        ifActive,
        claimDragStream,
        freeDragStream,
        unclaimDragStream,
        getGlobalDragContext,
        claimed,
        active,
    };
}

function onDocument(f: Function) {
    return (...args: any[]) => f(document, ...args);
}

function fireOnlyWhenCoordinatesChange(f: Function) {
    let x = NaN,
        y = NaN;
    return (element: HTMLElement | Document, callback: Function, options: any) => {
        return f(
            element,
            (dragContext: DragContext, event: PointerEvent) => {
                if (dragContext.x != x || dragContext.y != y) {
                    x = dragContext.x;
                    y = dragContext.y;
                    callback(dragContext, event);
                }
            },
            options,
        );
    };
}

window.addEventListener(
    "pointerup",
    (event) => {
        if (activeDragContext) {
            activeDragContext = null;
        }
    },
    { passive: true },
);
